import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { acceptInvitation, previewInvitation } from '../api/invitations.js'
import { useAuth } from '../auth/AuthProvider.jsx'
import { firstError, formatDateTime } from '../utils/format.js'

/**
 * Public invitation acceptance page. Path: /accept-invite/:token
 *
 * Flow:
 *   1. GET /api/invitations/<token>/  — show who this invite is for.
 *   2. Clinician enters (optionally) a username, a password, and optional phone.
 *   3. POST /api/invitations/<token>/accept/  — server creates User +
 *      DoctorProfile and returns JWT pair; we call signInWithTokens to
 *      hydrate the AuthProvider, then redirect to /doctor.
 */
export default function AcceptInvite() {
  const { token } = useParams()
  const { signInWithTokens } = useAuth()
  const nav = useNavigate()

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => previewInvitation(token),
    retry: false,
  })
  const inv = preview.data

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  async function onSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setErr('Passwords do not match.')
      return
    }
    setBusy(true)
    try {
      const resp = await acceptInvitation(token, {
        username: username || undefined,
        password,
        phone_number: phone || undefined,
      })
      await signInWithTokens({ access: resp.access, refresh: resp.refresh })
      nav('/', { replace: true })  // RoleHome routes to /doctor
    } catch (e) {
      setErr(firstError(e, 'Could not accept the invitation.'))
    } finally {
      setBusy(false)
    }
  }

  // ---- states -------------------------------------------------------------

  if (preview.isLoading) {
    return <Shell><p className="text-sm text-slate-500">Loading invitation…</p></Shell>
  }

  if (preview.isError || !inv) {
    return (
      <Shell>
        <h2 className="text-lg font-semibold text-slate-800">Invitation link is not valid</h2>
        <p className="text-sm text-slate-500 mt-1">
          This link is unknown, has been revoked, or has already been used. Please
          ask your administrator to issue a fresh invitation.
        </p>
        <Link to="/login" className="inline-block mt-4 text-wantok-700 underline text-sm">
          Return to sign-in
        </Link>
      </Shell>
    )
  }

  if (inv.effective_status !== 'pending') {
    return (
      <Shell>
        <h2 className="text-lg font-semibold text-slate-800">
          This invitation has {inv.effective_status === 'expired' ? 'expired' : `been ${inv.effective_status}`}
        </h2>
        <p className="text-sm text-slate-500 mt-2">
          Invited: <b>{inv.first_name} {inv.last_name}</b> &lt;{inv.email}&gt;
        </p>
        <p className="text-sm text-slate-500 mt-3">
          Ask your administrator to issue a fresh invitation.
        </p>
        <Link to="/login" className="inline-block mt-4 text-wantok-700 underline text-sm">
          Return to sign-in
        </Link>
      </Shell>
    )
  }

  // ---- happy path ---------------------------------------------------------

  return (
    <Shell>
      <h2 className="text-lg font-semibold text-slate-800">Welcome, Dr {inv.first_name} {inv.last_name}</h2>
      <p className="text-sm text-slate-500 mt-1">
        You've been invited to join <b>Wantok Telehealth</b> as a{' '}
        {inv.user_type === 'specialist' ? 'specialist' : 'doctor'}
        {inv.specialty ? ` (${inv.specialty})` : ''}
        {inv.place_of_work ? ` at ${inv.place_of_work}` : ''}.
      </p>

      <div className="mt-3 text-xs text-slate-500 space-y-0.5 bg-slate-50 border border-slate-200 rounded p-2">
        {inv.license_number && <div>MSB-PNG registration: <span className="font-mono">{inv.license_number}</span></div>}
        {inv.expires_at && <div>Invitation valid until: {formatDateTime(inv.expires_at)}</div>}
      </div>

      <form onSubmit={onSubmit} className="mt-5 space-y-3">
        <label className="text-xs text-slate-600 flex flex-col gap-1">
          <span>Username (optional)</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Auto-generated if blank"
            autoComplete="username"
            className="border rounded px-2 py-1.5 text-sm"
          />
        </label>

        <label className="text-xs text-slate-600 flex flex-col gap-1">
          <span>Contact phone (optional)</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+675 …"
            autoComplete="tel"
            className="border rounded px-2 py-1.5 text-sm"
          />
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            <span>Password (min 8) *</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </label>
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            <span>Confirm password *</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
              className="border rounded px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        {err && (
          <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-sm">
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full bg-wantok-700 hover:bg-wantok-800 text-white rounded py-2 text-sm disabled:opacity-60"
        >
          {busy ? 'Creating account…' : 'Accept invitation & sign in'}
        </button>
      </form>

      <p className="text-[11px] text-slate-400 text-center mt-3">
        By accepting you confirm that you are the named clinician and that
        the registration details above are correct.
      </p>
    </Shell>
  )
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <h1 className="text-xl font-semibold text-wantok-800">Wantok Telehealth</h1>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  )
}
